import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { API_BASE_URL, LOGIN_API_KEY, ATHENA_SESSION_TOKEN } from '../../lib/config'
import { useAuth } from '../../components/auth/AuthProvider'

interface BusinessInfo {
  name?: string
  about?: string
  businessType?: string
  natureOfBusiness?: string
  industry?: string
  location?: string
  subindustry?: string
}

const emptyBusinessInfo: BusinessInfo = {
  name: '',
  about: '',
  businessType: '',
  natureOfBusiness: '',
  industry: '',
  location: '',
  subindustry: '',
}

export function LoginPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const navigate = useNavigate()
  const { refreshSession, isSignedIn, loading: authLoading } = useAuth()
  const [passcodeUnlocked, setPasscodeUnlocked] = useState(() =>
    typeof sessionStorage !== 'undefined' && sessionStorage.getItem('athena-passcode-unlocked') === 'true'
  )
  const [passcode, setPasscode] = useState('')
  const [passcodeError, setPasscodeError] = useState('')
  const [passcodeLoading, setPasscodeLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [tokenLoading, setTokenLoading] = useState(!!token)
  const [showBusinessInfo, setShowBusinessInfo] = useState(false)
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>(emptyBusinessInfo)

  useEffect(() => {
    if (!authLoading && isSignedIn && !token) navigate('/ochat', { replace: true })
  }, [authLoading, isSignedIn, token, navigate])

  // Strip token from URL immediately so it is not visible in address bar (including in iframe)
  useEffect(() => {
    if (!token) {
      setTokenLoading(false)
      return
    }
    setTokenLoading(true)
    const hash = window.location.hash || '#/'
    const [hashPath, hashQuery] = hash.split('?')
    const params = new URLSearchParams(hashQuery || '')
    params.delete('token')
    const newHash = hashPath + (params.toString() ? `?${params.toString()}` : '')
    window.history.replaceState(null, '', window.location.pathname + window.location.search + newHash)

    let cancelled = false
    const redeemToken = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}auth/login-with-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        })
        if (cancelled) return
        const data = await res.json().catch(() => ({}))
        if (res.ok && data.ok) {
          if (data.token) localStorage.setItem(ATHENA_SESSION_TOKEN, data.token)
          refreshSession()
          navigate('/ochat', { replace: true })
          return
        }
        // Invalid, expired, or access-denied token → land on session-timeout
        navigate('/session-timeout', { replace: true })
      } catch {
        if (!cancelled) navigate('/session-timeout', { replace: true })
      } finally {
        if (!cancelled) setTokenLoading(false)
      }
    }
    redeemToken()
    return () => { cancelled = true }
  }, [token, navigate, refreshSession])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!LOGIN_API_KEY) {
      setError('Login is not configured. Set VITE_LOGIN_API_KEY.')
      return
    }
    if (showBusinessInfo && !businessInfo.name?.trim()) {
      setError('Business name is required when adding business details.')
      return
    }
    setLoading(true)
    try {
      const business_info: Record<string, string> = {}
      if (showBusinessInfo) {
        (['name', 'about', 'businessType', 'natureOfBusiness', 'industry', 'location', 'subindustry'] as const).forEach((key) => {
          const v = businessInfo[key]
          if (v != null && String(v).trim() !== '') business_info[key] = String(v).trim()
        })
      }
      const payload: Record<string, unknown> = { email: email.trim(), business_info }
      const personName = name.trim() || (showBusinessInfo && businessInfo.name?.trim())
      if (personName) payload.name = personName
      const res = await fetch(`${API_BASE_URL}auth/email-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': LOGIN_API_KEY,
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.token) {
        navigate(`/login?token=${encodeURIComponent(data.token)}`, { replace: true })
        return
      }
      setError(data.detail || data.error || 'Request failed')
    } catch {
      setError(
        'Cannot reach the server. Make sure the backend is running (e.g. pnpm backend on port 8000).'
      )
    } finally {
      setLoading(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    fontSize: 14,
    color: 'var(--text)',
    background: 'var(--white)',
    border: '1px solid var(--bd)',
    borderRadius: 9,
    outline: 'none',
  }

  const handlePasscodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setPasscodeError('')
    setPasscodeLoading(true)
    try {
      const res = await fetch(`${API_BASE_URL}auth/check-passcode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ passcode: passcode.trim() }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) {
        setPasscodeUnlocked(true)
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('athena-passcode-unlocked', 'true')
        }
        setPasscode('')
        return
      }
      setPasscodeError(data.error || 'Invalid passcode')
    } catch {
      setPasscodeError('Could not verify passcode. Try again.')
    } finally {
      setPasscodeLoading(false)
    }
  }

  // When token is in URL or we are redeeming a token, show only loading — never show passcode or login form
  if (token || tokenLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          background: 'var(--bg)',
        }}
      >
        <p style={{ fontSize: 14, color: 'var(--muted)' }}>Signing you in…</p>
        {error && <p style={{ fontSize: 12, color: 'var(--error)' }}>{error}</p>}
      </div>
    )
  }

  if (!passcodeUnlocked) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--white)',
          padding: 24,
        }}
      >
        <div style={{ width: '100%', maxWidth: 380 }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <h1
              style={{
                fontFamily: "'Playfair Display',serif",
                fontSize: 26,
                fontWeight: 500,
                color: 'var(--navy)',
              }}
            >
              Athena
            </h1>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>Enter passcode to continue</p>
          </div>
          <form onSubmit={handlePasscodeSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label
                htmlFor="passcode"
                style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}
              >
                Passcode
              </label>
              <input
                id="passcode"
                type="password"
                autoComplete="off"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="Enter passcode"
                style={inputStyle}
              />
            </div>
            {passcodeError && (
              <p style={{ fontSize: 12, color: 'var(--error)', margin: 0 }} role="alert">
                {passcodeError}
              </p>
            )}
            <button
              type="submit"
              disabled={passcodeLoading}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: 14,
                fontWeight: 500,
                color: 'var(--white)',
                background: 'var(--navy)',
                border: 'none',
                borderRadius: 12,
                cursor: passcodeLoading ? 'not-allowed' : 'pointer',
                boxShadow: 'var(--sh-sm)',
              }}
            >
              {passcodeLoading ? 'Checking…' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--white)',
        padding: 24,
      }}
    >
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <h1
            style={{
              fontFamily: "'Playfair Display',serif",
              fontSize: 26,
              fontWeight: 500,
              color: 'var(--navy)',
            }}
          >
            Athena
          </h1>
          <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>Sign in to continue</p>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label
              htmlFor="email"
              style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              style={inputStyle}
            />
          </div>
          <div>
            <label
              htmlFor="name"
              style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}
            >
              Your name (optional)
            </label>
            <input
              id="name"
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Jane Doe"
              style={inputStyle}
            />
          </div>
          <div>
            <button
              type="button"
              onClick={() => setShowBusinessInfo((v) => !v)}
              style={{
                fontSize: 12,
                color: 'var(--blue)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
              }}
            >
              {showBusinessInfo ? 'Hide business details' : 'Add business details (optional)'}
            </button>
            {showBusinessInfo && (
              <div
                style={{
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: '1px solid var(--bd2)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                {(
                  [
                    { key: 'name' as const, placeholder: 'Business or your name' },
                    { key: 'about' as const, placeholder: 'Brief description' },
                    { key: 'businessType' as const, placeholder: 'e.g. B2B, B2C' },
                    { key: 'natureOfBusiness' as const, placeholder: 'e.g. Training, Consulting' },
                    { key: 'industry' as const, placeholder: 'e.g. Education, Retail' },
                    { key: 'subindustry' as const, placeholder: 'Optional' },
                    { key: 'location' as const, placeholder: 'City, Country' },
                  ] as const
                ).map(({ key, placeholder }) => (
                  <div key={key}>
                    <label
                      htmlFor={`biz-${key}`}
                      style={{
                        display: 'block',
                        fontSize: 12,
                        fontWeight: 500,
                        color: 'var(--text)',
                        marginBottom: 6,
                      }}
                    >
                      {key === 'name' ? 'Name *' : key === 'about' ? 'About' : key === 'businessType' ? 'Business type' : key === 'natureOfBusiness' ? 'Nature of business' : key === 'industry' ? 'Industry' : key === 'subindustry' ? 'Subindustry' : 'Location'}
                    </label>
                    <input
                      id={`biz-${key}`}
                      value={businessInfo[key] ?? ''}
                      onChange={(e) => setBusinessInfo((b) => ({ ...b, [key]: e.target.value }))}
                      placeholder={placeholder}
                      style={inputStyle}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          {error && (
            <p style={{ fontSize: 12, color: 'var(--error)', margin: 0 }} role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: 14,
              fontWeight: 500,
              color: 'var(--white)',
              background: 'var(--navy)',
              border: 'none',
              borderRadius: 12,
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: 'var(--sh-sm)',
            }}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  )
}
